/**
 * Main validator
 * Orchestrates all validation steps
 */

import { ValidationResult, ValidationMessage, ModSchema, ParsedObject, SchemaData, ClassSchema } from './types.js';
import { ModParser } from './parser.js';
import { PropertyValidator } from './property-validator.js';
import { findSimilar, MAX_EDIT_DISTANCE } from './string-similarity.js';
import modSchemaData from './mod-schema.json' with { type: 'json' };

export class ModValidator {
  private schema: ModSchema;
  private typeAliases: Record<string, string>;
  private functionalAliases: Record<string, string>;
  private propertyValidator = new PropertyValidator();
  private parsedObjectsCache: Map<string, ParsedObject[]> = new Map();

  constructor() {
    const data = modSchemaData as SchemaData;
    this.schema = data.schema;
    this.typeAliases = data.typeAliases;
    this.functionalAliases = data.functionalAliases;
  }

  private resolveTypeAlias(typeName: string): string {
    return this.typeAliases[typeName] || typeName;
  }

  validate(content: string, filePath: string): ValidationResult {
    const parser = new ModParser(content, filePath);

    const { objects, errors: parseErrors } = parser.parse();

    // Cache parsed objects for cross-file validation
    this.parsedObjectsCache.set(filePath, objects);

    const objMessages = objects.flatMap(obj => this.validateObject(obj));
    const structureMessages = this.validateActionStructures(objects);
    // NOTE: checkDuplicateIds now runs separately via getCrossFileValidationMessages()

    const allMessages = [...parseErrors, ...objMessages, ...structureMessages];

    const errors = allMessages.filter(m => m.severity === 'error');
    const warnings = allMessages.filter(m => m.severity === 'warning');
    const hints = allMessages.filter(m => m.severity === 'hint');
    const info = allMessages.filter(m => m.severity === 'info');

    return {
      errors,
      warnings,
      hints,
      info,
    };
  }

  /**
   * Get validation messages that require checking across all files
   * (e.g., duplicate IDs across files)
   */
  getCrossFileValidationMessages(): ValidationMessage[] {
    const allObjects = Array.from(this.parsedObjectsCache.values()).flat();
    return this.checkDuplicateIds(allObjects);
  }

  /**
   * Clear the entire cache
   */
  clearCache(): void {
    this.parsedObjectsCache.clear();
  }

  /**
   * Remove a specific file from the cache
   */
  removeFromCache(filePath: string): void {
    this.parsedObjectsCache.delete(filePath);
  }

  /**
   * Get the parsed objects cache for UI features (like object viewer)
   */
  getParsedObjectsCache(): Map<string, ParsedObject[]> {
    return this.parsedObjectsCache;
  }

  /**
   * Resolve a functional alias to normalize spelling variations
   * (e.g., "ActionAOE" normalizes to "ActionAoE")
   * This does NOT resolve type aliases (e.g., AvAffecterAoE stays as-is)
   */
  resolveFunctionalAlias(typeName: string): string {
    return this.functionalAliases[typeName] || typeName;
  }

  /**
   * Check if two ParsedObjects are identical (have the same properties with the same values)
   */
  private areObjectsIdentical(obj1: ParsedObject, obj2: ParsedObject): boolean {
    // Different number of properties means not identical
    if (obj1.properties.size !== obj2.properties.size) {
      return false;
    }

    // Check if all properties in obj1 exist in obj2 with the same value
    for (const [propName, propInfo1] of obj1.properties) {
      const propInfo2 = obj2.properties.get(propName);
      if (!propInfo2) {
        return false; // Property missing in obj2
      }
      // Normalize whitespace for comparison
      if (propInfo1.value.trim() !== propInfo2.value.trim()) {
        return false; // Different values
      }
    }

    return true;
  }

  /**
   * Group objects by their content (identical objects are grouped together)
   * Returns an array of groups, where each group contains objects with identical properties
   */
  private groupIdenticalObjects(objs: ParsedObject[]): ParsedObject[][] {
    const groups: ParsedObject[][] = [];

    for (const obj of objs) {
      // Find a group where this object is identical to the first object in that group
      let foundGroup = false;
      for (const group of groups) {
        const firstInGroup = group[0];
        if (firstInGroup && this.areObjectsIdentical(obj, firstInGroup)) {
          group.push(obj);
          foundGroup = true;
          break;
        }
      }

      // If no matching group found, create a new group
      if (!foundGroup) {
        groups.push([obj]);
      }
    }

    return groups;
  }

  private checkDuplicateIds(objects: ParsedObject[]): ValidationMessage[] {
    const messages: ValidationMessage[] = [];
    const objectIds: Map<string, Map<string, ParsedObject[]>> = new Map();

    // Collect all objects with IDs, grouped by type and ID
    for (const obj of objects) {
      const classSchema = this.schema[this.resolveTypeAlias(obj.type)];
      if (classSchema?.category !== 'definition') {
        continue;
      }

      const objId = obj.properties.get('ID')?.value;
      if (!objId) {
        continue;
      }

      // If cloning from the same ID, that means we're extending it not duplicating it.
      if (classSchema.supportsCloneFrom && obj.properties.get('cloneFrom')?.value === objId) {
        continue;
      }

      // Treat things like ActionAoE and ActionAOE as the same.
      const resolvedType = this.resolveFunctionalAlias(obj.type);
      if (!objectIds.has(resolvedType)) {
        objectIds.set(resolvedType, new Map());
      }
      const typeIds = objectIds.get(resolvedType);
      if (!typeIds) {
        throw new Error(`Type IDs for ${resolvedType} not initialized`);
      }

      const objsWithSameId = typeIds.get(objId);
      typeIds.set(objId, [...(objsWithSameId ?? []), obj]);
    }

    // For each ID used more than once, group by identical content and create messages
    for (const [resolvedType, typeIds] of objectIds) {
      for (const [objId, objs] of typeIds) {
        if (objs.length <= 1) {
          continue; // Not a duplicate
        }

        // Group objects by their content
        const groups = this.groupIdenticalObjects(objs);

        // If there's only one group, all objects are identical
        if (groups.length === 1) {
          const group = groups[0];
          if (!group) {
            throw new Error('Group should not be empty');
          }

          // Create corrections for all uses
          const corrections = group.map(obj => {
            const idProp = obj.properties.get('ID');
            if (!idProp) {
              throw new Error(`Object missing ID property: ${obj.type}`);
            }
            return {
              filePath: idProp.filePath,
              startLine: idProp.valueStartLine,
              startColumn: idProp.valueStartColumn,
              endLine: idProp.valueEndLine,
              endColumn: idProp.valueEndColumn,
              replacementText: idProp.value, // Same as original (for navigation only)
              displayText: `${idProp.filePath}:${idProp.valueStartLine}`,
            };
          });

          // Use the first occurrence's location for the message
          const firstObj = group[0];
          if (!firstObj) {
            throw new Error('Group should not be empty');
          }
          const firstIdProp = firstObj.properties.get('ID');
          if (!firstIdProp) {
            throw new Error(`First object missing ID property: ${firstObj.type}`);
          }

          messages.push({
            severity: 'info',
            message: `ID '${objId}' for ${resolvedType} has ${group.length} identical copies`,
            filePath: firstIdProp.filePath,
            line: firstIdProp.valueStartLine,
            suggestion: 'Consider removing redundant copies:',
            correctionIcon: '🎯',
            corrections,
            isCrossFile: true,
          });
        } else {
          // Multiple groups means there are conflicting definitions
          // Report on each group separately
          for (const group of groups) {
            if (!group || group.length === 0) {
              continue;
            }

            // Find the largest group (most common version) to use as reference
            const largestGroup = groups.reduce((largest, current) =>
              current.length > largest.length ? current : largest
            );

            // Find properties that differ from the largest group
            const conflictingProperties = new Set<string>();
            const firstInGroup = group[0];
            if (!firstInGroup) {
              throw new Error('Group should not be empty');
            }

            // Only compare against the largest group (unless this IS the largest group)
            if (largestGroup.length > 1 && group !== largestGroup) {
              const firstInLargest = largestGroup[0];
              if (firstInLargest) {
                // Check each property for differences from the largest group
                for (const [propName, propInfo] of firstInGroup.properties) {
                  if (propName === 'ID') continue; // Skip ID since it's the same

                  const largestPropInfo = firstInLargest.properties.get(propName);
                  if (!largestPropInfo) {
                    // Property exists in this group but not in largest group
                    conflictingProperties.add(propName);
                  } else if (propInfo.value.trim() !== largestPropInfo.value.trim()) {
                    // Property has different value
                    conflictingProperties.add(propName);
                  }
                }

                // Check for properties in largest group that don't exist in this group
                for (const propName of firstInLargest.properties.keys()) {
                  if (propName === 'ID') continue;
                  if (!firstInGroup.properties.has(propName)) {
                    conflictingProperties.add(propName);
                  }
                }
              }
            } else {
              // This is the largest group, or all groups are size 1
              // Compare against all other groups to find what's different
              for (const otherGroup of groups) {
                if (otherGroup === group) continue;
                const firstInOther = otherGroup[0];
                if (!firstInOther) continue;

                // Check each property for differences
                for (const [propName, propInfo] of firstInGroup.properties) {
                  if (propName === 'ID') continue;

                  const otherPropInfo = firstInOther.properties.get(propName);
                  if (!otherPropInfo) {
                    conflictingProperties.add(propName);
                  } else if (propInfo.value.trim() !== otherPropInfo.value.trim()) {
                    conflictingProperties.add(propName);
                  }
                }

                // Check for properties in other group that don't exist in this group
                for (const propName of firstInOther.properties.keys()) {
                  if (propName === 'ID') continue;
                  if (!firstInGroup.properties.has(propName)) {
                    conflictingProperties.add(propName);
                  }
                }
              }
            }

            // Create corrections based on message type
            let corrections;

            if (group.length > 1) {
              // Multiple identical copies - jump to ID locations
              corrections = group.map(obj => {
                const idProp = obj.properties.get('ID');
                if (!idProp) {
                  throw new Error(`Object missing ID property: ${obj.type}`);
                }
                return {
                  filePath: idProp.filePath,
                  startLine: idProp.valueStartLine,
                  startColumn: idProp.valueStartColumn,
                  endLine: idProp.valueEndLine,
                  endColumn: idProp.valueEndColumn,
                  replacementText: idProp.value,
                  displayText: `${idProp.filePath}:${idProp.valueStartLine}`,
                };
              });
            } else {
              // Single conflicting object - jump to conflicting properties
              corrections = group.flatMap(obj => {
                const propCorrections = [];

                if (conflictingProperties.size > 0) {
                  // Jump to each conflicting property value
                  for (const propName of conflictingProperties) {
                    const propInfo = obj.properties.get(propName);
                    if (propInfo) {
                      propCorrections.push({
                        filePath: propInfo.filePath,
                        startLine: propInfo.valueStartLine,
                        startColumn: propInfo.valueStartColumn,
                        endLine: propInfo.valueEndLine,
                        endColumn: propInfo.valueEndColumn,
                        replacementText: propInfo.value, // Same as original value (for navigation)
                        displayText: propName,
                      });
                    }
                  }
                } else {
                  // Fallback to ID if we can't identify specific conflicts
                  const idProp = obj.properties.get('ID');
                  if (idProp) {
                    propCorrections.push({
                      filePath: idProp.filePath,
                      startLine: idProp.valueStartLine,
                      startColumn: idProp.valueStartColumn,
                      endLine: idProp.valueEndLine,
                      endColumn: idProp.valueEndColumn,
                      replacementText: idProp.value,
                      displayText: `${idProp.filePath}:${idProp.valueStartLine}`,
                    });
                  }
                }

                return propCorrections;
              });
            }

            const firstObj = group[0];
            if (!firstObj) {
              throw new Error('Group should not be empty');
            }
            const firstIdProp = firstObj.properties.get('ID');
            if (!firstIdProp) {
              throw new Error(`First object missing ID property: ${firstObj.type}`);
            }

            if (group.length > 1) {
              // Multiple identical copies in this group
              messages.push({
                severity: 'info',
                message: `ID '${objId}' for ${resolvedType} has ${group.length} identical copies (part of ${objs.length} total conflicting definitions)`,
                filePath: firstIdProp.filePath,
                line: firstIdProp.valueStartLine,
                context: `This is one of ${groups.length} different versions.`,
                suggestion: 'Identical instances:',
                correctionIcon: '🎯',
                corrections,
                isCrossFile: true,
              });
            } else {
              // Single object in this group (unique definition among the duplicates)
              messages.push({
                severity: 'warning',
                message: `ID '${objId}' for ${resolvedType} conflicts with ${objs.length - 1} other definition(s)`,
                filePath: firstIdProp.filePath,
                line: firstIdProp.valueStartLine,
                suggestion: `Conflicting properties:`,
                correctionIcon: '🎯',
                corrections,
                isCrossFile: true,
              });
            }
          }
        }
      }
    }

    return messages;
  }

  private validateObject(obj: ParsedObject): ValidationMessage[] {
    const messages: ValidationMessage[] = [];

    const resolvedType = this.resolveTypeAlias(obj.type);
    const classSchema = this.schema[resolvedType];

    if (!classSchema) {
      const allTypes = [...Object.keys(this.schema), ...Object.keys(this.typeAliases)];
      const similar = findSimilar(obj.type, allTypes, MAX_EDIT_DISTANCE);
      const corrections = similar.map(s => ({
        filePath: obj.filePath,
        startLine: obj.typeStartLine,
        startColumn: obj.typeStartColumn,
        endLine: obj.typeStartLine,
        endColumn: obj.typeEndColumn,
        replacementText: s.value,
      }));

      messages.push({
        severity: 'error',
        message: `Unknown object type: ${obj.type}`,
        filePath: obj.filePath,
        line: obj.startLine,
        context: 'This object type is not recognized',
        corrections,
      });
      return messages;
    }

    if (classSchema.category === 'definition' && !obj.properties.has('ID')) {
      messages.push({
        severity: 'error',
        message: `Object type ${obj.type} requires an ID property`,
        filePath: obj.filePath,
        line: obj.startLine,
        suggestion: 'Add: ID = yourUniqueID;',
      });
    }

    if (obj.properties.has('cloneFrom') && classSchema.supportsCloneFrom === false) {
      messages.push({
        severity: 'warning',
        message: `Object type ${obj.type} does not support cloneFrom`,
        filePath: obj.filePath,
        line: obj.startLine,
        context: 'The cloneFrom property will be ignored',
      });
    }

    messages.push(...this.validateProperties(obj, classSchema, resolvedType));

    // Special validation for TriggerEffect objects
    if (resolvedType === 'TriggerEffect') {
      messages.push(...this.validateTriggerEffect(obj));
    }

    return messages;
  }

  private validateProperties(
    obj: ParsedObject,
    classSchema: ClassSchema,
    resolvedTypeName: string
  ): ValidationMessage[] {
    const messages: ValidationMessage[] = [];
    const knownFields = new Map<string, string>();
    const patternFields: Array<{
      base: string;
      type: string;
      suffix: 'number' | 'plus';
    }> = [];

    // Build map of known fields and pattern fields
    // Real fields take precedence over virtual properties
    for (const field of classSchema.fields) {
      if (field.pattern && field.name.endsWith('N')) {
        // This is a pattern field like "bodyPartN" - accepts numbered properties
        const baseName = field.name.substring(0, field.name.length - 1);
        patternFields.push({
          base: baseName,
          type: field.type,
          suffix: 'number',
        });
      } else if (field.pattern && field.name.endsWith('+')) {
        // This is a pattern field like "topX+" - accepts + suffixes
        const baseName = field.name.substring(0, field.name.length - 1);
        patternFields.push({
          base: baseName,
          type: field.type,
          suffix: 'plus',
        });
      } else {
        // Only add if not already present (real fields come before virtual in the array)
        // or if this is a real field (not virtual) - prefer real fields over virtual
        const isVirtual = field.virtual === true;
        if (!knownFields.has(field.name) || !isVirtual) {
          knownFields.set(field.name, field.type);
        }
      }
    }

    // Validate each property
    for (const [propName, propInfo] of obj.properties) {
      const propValue = propInfo.value;

      // Remove the ! prefix and + suffixes for lookup
      let cleanPropName = propName;
      if (cleanPropName.startsWith('!')) {
        cleanPropName = cleanPropName.substring(1);
      }
      while (cleanPropName.endsWith('+')) {
        cleanPropName = cleanPropName.substring(0, cleanPropName.length - 1);
      }

      let fieldType = knownFields.get(cleanPropName);

      // Check if it matches a pattern field
      if (!fieldType) {
        for (const pattern of patternFields) {
          if (pattern.suffix === 'number') {
            // Pattern like "bodyPart1", "bodyPart2" - base name + number
            if (cleanPropName.startsWith(pattern.base) && /\d+$/.test(cleanPropName)) {
              fieldType = pattern.type;
              break;
            }
          } else if (pattern.suffix === 'plus') {
            // Pattern like "topX", "topX+", "topX++" - base name + optional + suffixes
            if (
              cleanPropName === pattern.base ||
              (cleanPropName.startsWith(pattern.base) && /^\+*$/.test(cleanPropName.substring(pattern.base.length)))
            ) {
              fieldType = pattern.type;
              break;
            }
          }
        }
      }

      if (!fieldType) {
        // Skip property name validation for Actor type (it dynamically handles extra properties)
        if (resolvedTypeName === 'Actor') {
          // For Actor, skip the unknown property check entirely
          continue;
        }

        // Unknown property - likely a typo that will break the mod
        const typeDisplay = resolvedTypeName !== obj.type ? `${obj.type} (${resolvedTypeName})` : obj.type;

        // Find similar property names
        const allProperties = Array.from(knownFields.keys());
        const similar = findSimilar(cleanPropName, allProperties, MAX_EDIT_DISTANCE);
        const corrections = similar.map(s => ({
          filePath: propInfo.filePath,
          startLine: propInfo.nameStartLine,
          startColumn: propInfo.nameStartColumn,
          endLine: propInfo.nameStartLine,
          endColumn: propInfo.nameEndColumn,
          replacementText: s.value,
        }));

        messages.push({
          severity: 'hint',
          message: `Unknown property '${cleanPropName}' for ${typeDisplay}`,
          filePath: propInfo.filePath,
          line: propInfo.nameStartLine,
          context: `Value: ${propValue}`,
          suggestion: corrections.length === 0 ? 'Check for typos in property name' : undefined,
          corrections,
        });
        continue;
      }

      // Special case: ActorValueAffecter.magnitude should be validated as task string
      // when actorValue property equals "task" or "trigger"
      if (resolvedTypeName === 'ActorValueAffecter' && cleanPropName === 'magnitude') {
        const actorValueProp = obj.properties.get('actorValue');
        const actorValue = actorValueProp?.value.trim();
        if (actorValueProp && (actorValue === 'task' || actorValue === 'trigger')) {
          // ERROR: magnitude must not be empty when actorValue = "task" or "trigger"
          if (propValue.trim() === '') {
            messages.push({
              severity: 'error',
              message: `ActorValueAffecter with actorValue="${actorValue}" requires non-empty magnitude`,
              filePath: propInfo.filePath,
              line: propInfo.valueStartLine,
              context: 'The magnitude property should contain a task string (e.g., "action,actionID")',
            });
            continue;
          }

          // Validate as task string instead of Formula
          const taskMessages = this.propertyValidator.validateTaskString(
            propValue,
            propInfo,
            resolvedTypeName,
            cleanPropName
          );
          messages.push(...taskMessages);
          continue; // Skip normal Formula validation
        }
      }

      // Validate property type
      // Use cleanPropName so corrections reference the base property name without + suffixes
      const typeMessages = this.propertyValidator.validateProperty(
        cleanPropName,
        propValue,
        fieldType,
        propInfo,
        resolvedTypeName
      );

      messages.push(...typeMessages);
    }

    return messages;
  }

  /**
   * Validate TriggerEffect object properties
   *
   * TriggerEffect can be specified in two ways:
   * 1. Using taskString property (validated as a task string)
   * 2. Using individual properties: effectID, sValue, sValue2, fValue, xValue, yValue, bValue1, bValue2, delay, fReq
   *
   * All TriggerEffect properties have default values, so parameter counting validation doesn't apply:
   * - sValue = "" (default)
   * - sValue2 = "" (default)
   * - fValue = 0f (default)
   * - xValue = -1 (default)
   * - yValue = -1 (default)
   * - bValue1 = false (default)
   * - bValue2 = false (default)
   * - delay = 0f (default)
   * - fReq = "1" (default)
   *
   * The validation only checks that effectID is a valid task name.
   *
   * Note: @-parameters do NOT work in TriggerEffect (no @ prefix handling in TriggerEffect.cs)
   */
  private validateTriggerEffect(obj: ParsedObject): ValidationMessage[] {
    const messages: ValidationMessage[] = [];

    // If taskString is set, it takes precedence (already validated by property validator)
    const taskString = obj.properties.get('taskString');
    const effectID = obj.properties.get('effectID');

    if (taskString && taskString.value.trim() !== '' && effectID && effectID.value.trim() !== '') {
      // Both are specified - warn that effectID will be ignored
      messages.push({
        severity: 'warning',
        message: 'Both taskString and effectID are specified on TriggerEffect',
        context: 'taskString takes precedence and effectID will be ignored',
        filePath: effectID.filePath,
        line: effectID.nameStartLine,
      });
    }

    if (taskString && taskString.value.trim() !== '') {
      return messages;
    }

    // Get effectID - this is the task name
    if (!effectID || effectID.value.trim() === '') {
      // No effectID and no taskString - this TriggerEffect is incomplete
      messages.push({
        severity: 'error',
        message: 'TriggerEffect has neither effectID nor taskString',
        filePath: obj.filePath,
        line: obj.startLine,
      });
      return messages;
    }

    // Validate effectID as a task name (with typo suggestions)
    // Note: We don't validate parameter counts because all TriggerEffect properties have defaults
    const effectIDMessages = this.propertyValidator.validateTaskName(effectID.value, effectID);
    messages.push(...effectIDMessages);

    return messages;
  }

  getKnownObjectTypes(): string[] {
    return Object.keys(this.schema);
  }

  getObjectSchema(type: string): ClassSchema | null {
    return this.schema[type] || null;
  }

  /**
   * Validate Action structure:
   * [Action] must be followed by [ActionAoE], then one or more [AvAffecter]+[AvAffecterAoE] pairs
   * All IDs must match the Action's ID
   */
  private validateActionStructures(objects: ParsedObject[]): ValidationMessage[] {
    const messages: ValidationMessage[] = [];

    const validateActionId = (obj: ParsedObject, actionId: string): boolean => {
      const idProp = obj.properties.get('ID');
      if (!idProp) {
        messages.push({
          severity: 'error',
          message: `${obj.type} for Action '${actionId}' is missing ID property`,
          filePath: obj.filePath,
          line: obj.startLine,
          suggestion: `Add: ID=${actionId};`,
          suggestionIsAction: true,
          correctionIcon: '🔧',
          corrections: [
            {
              filePath: obj.filePath,
              startLine: obj.startLine,
              startColumn: obj.typeBracketEndColumn,
              endLine: obj.startLine,
              endColumn: obj.typeBracketEndColumn,
              replacementText: ` ID=${actionId};`,
            },
          ],
        });
        return false;
      }

      if (idProp.value !== actionId) {
        messages.push({
          severity: 'error',
          message: `${obj.type} ID '${idProp.value}' does not match Action ID '${actionId}'`,
          filePath: obj.filePath,
          line: idProp.valueStartLine,
          corrections: [
            {
              filePath: idProp.filePath,
              startLine: idProp.valueStartLine,
              startColumn: idProp.valueStartColumn,
              endLine: idProp.valueEndLine,
              endColumn: idProp.valueEndColumn,
              replacementText: actionId,
            },
          ],
        });
        return false;
      }
      return true;
    };

    const validateAction = (action: ParsedObject): boolean => {
      const actionId = action.properties.get('ID')?.value;
      if (!actionId) {
        // We have another check for ID existing elsewhere.
        return false;
      }

      // If we're cloning from another Action, we don't need to worry about missing subobjects
      // because we'll be inheriting the cloned Actions's subobjects by default.
      const hasCloneFrom = !!action.properties.get('cloneFrom')?.value;

      if (!action.nextObject) {
        if (!hasCloneFrom) {
          messages.push({
            severity: 'error',
            message: `Action '${actionId}' must be followed by [ActionAoE]`,
            filePath: action.filePath,
            line: action.endLine,
          });
        }
        return false;
      }

      if (!validateActionAoE(action.nextObject, actionId, hasCloneFrom)) {
        return false;
      }

      // Check what type action.nextObject actually is
      const nextType = this.resolveFunctionalAlias(action.nextObject.type);

      // Determine the starting point for AvAffecter validation
      let currentObj: ParsedObject | null = null;
      let firstAvAffecter = true;

      if (nextType === 'ActionAoE') {
        // Normal case: ActionAoE exists, check for AvAffecter after it
        if (!action.nextObject.nextObject) {
          if (!hasCloneFrom) {
            messages.push({
              severity: 'error',
              message: `Action '${actionId}' must have at least one [AvAffecter] following after the [ActionAoE]`,
              filePath: action.filePath,
              line: action.endLine,
            });
          }
          return false;
        }
        currentObj = action.nextObject;
      } else {
        // ActionAoE was skipped (hasCloneFrom), action.nextObject is the first AvAffecter
        // Start validation from the Action itself since we need to process action.nextObject
        currentObj = action;
      }

      while ((currentObj = currentObj.nextObject)) {
        if (!validateAvAffecter(currentObj, actionId, firstAvAffecter, hasCloneFrom)) {
          return false;
        }

        if (!currentObj.nextObject) {
          messages.push({
            severity: 'error',
            message: `AvAffecter for Action '${actionId}' must be followed by [AvAffecterAoE]`,
            filePath: currentObj.filePath,
            line: currentObj.endLine,
          });
          return false;
        }

        currentObj = currentObj.nextObject;
        if (!validateAvAffecterAoE(currentObj, actionId)) {
          return false;
        }

        firstAvAffecter = false;
      }
      return true;
    };

    const validateActionAoE = (obj: ParsedObject, actionId: string, actionHasCloneFrom: boolean): boolean => {
      const nextType = this.resolveFunctionalAlias(obj.type);
      if (nextType !== 'ActionAoE') {
        if (actionHasCloneFrom) {
          return true;
        }
        messages.push({
          severity: 'error',
          message: `Action '${actionId}' must be followed by [ActionAoE], but found [${obj.type}]`,
          filePath: obj.filePath,
          line: obj.startLine,
        });
        return false;
      }

      if (!validateActionId(obj, actionId)) {
        return false;
      }

      return true;
    };

    const validateAvAffecter = (
      obj: ParsedObject,
      actionId: string,
      firstAvAffecter: boolean,
      actionHasCloneFrom: boolean
    ): boolean => {
      const nextType = this.resolveFunctionalAlias(obj.type);
      if (nextType !== 'AvAffecter') {
        if (!firstAvAffecter) {
          // If this isn't the first AvAffecter, being a different type isn't an error,
          // it just marks the end of this Action definition.
          return false;
        }
        if (actionHasCloneFrom) {
          return true;
        }
        messages.push({
          severity: 'error',
          message: `Action '${actionId}' expected at least one [AvAffecter], but found [${obj.type}]`,
          filePath: obj.filePath,
          line: obj.startLine,
        });
        return false;
      }

      if (!validateActionId(obj, actionId)) {
        return false;
      }

      return true;
    };

    const validateAvAffecterAoE = (obj: ParsedObject, actionId: string): boolean => {
      const nextType = this.resolveFunctionalAlias(obj.type);
      if (nextType !== 'AvAffecterAoE') {
        messages.push({
          severity: 'error',
          message: `AvAffecter for Action '${actionId}' must be followed by [AvAffecterAoE], but found [${obj.type}]`,
          filePath: obj.filePath,
          line: obj.startLine,
        });
        return false;
      }

      if (!validateActionId(obj, actionId)) {
        return false;
      }

      return true;
    };

    const actions = objects.filter(obj => this.resolveFunctionalAlias(obj.type) === 'Action');
    for (const action of actions) {
      validateAction(action);
    }

    return messages;
  }
}
